import { useCallback, useEffect, useState } from 'react';
import type { Sessao } from '../../auth';
import { dataHoraCurta } from '../../acesso';
import { useVeCusto } from '../../design/sigilo';
import { reais } from '../../produtos';
import { comoFalha, decidirAlerta, lerAlertas } from '../../validades';
import type { Alerta, Alertas as DadosDosAlertas } from '../../validades';
import { ErroDaLista, Esqueleto, mensagemDeFalha } from '../equipe/Equipe';
import type { Periodo } from '../../paineis';

/** O dia (Brasília) em que a decisão foi tomada, para o filtro de período. */
const diaDaDecisao = (iso: string) =>
  new Date(iso).toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });

/**
 * TELA 26-B — CENTRAL DE ALERTAS DE VALIDADE.
 *
 * Agrupados por evento, nunca um pop-up por produto. Lido ≠ tratado, e nenhum dos dois apaga
 * histórico. Tratar é sobre o ALERTA: o estado do lote continua o dele. Janela, criticidade e
 * destinatários ficam na configuração do sistema — esta tela só consome a regra vigente.
 */

type Aba = 'aberto' | 'lido' | 'tratado' | 'resolvidos';

const CRITICIDADE: Record<Alerta['criticidade'], string> = {
  critico: 'CRÍTICO',
  atencao: 'ATENÇÃO',
  cadastro: 'CADASTRO',
  dados: 'DADOS',
};

const ROTULO_DO_TIPO: Record<Alerta['tipo'], string> = {
  vencidos: 'Lotes vencidos com estoque',
  janela_30: 'Lotes na janela de 30 dias',
  sem_validade: 'Lotes sem validade informada',
  divergencia_validade: 'Divergência de validade',
};

export function Alertas({
  sessao,
  configura,
  aoMudar,
  periodo = null,
}: {
  sessao: Sessao;
  configura: boolean;
  aoMudar: () => void;
  /**
   * O filtro de período do módulo (dono, 05/10), pela data da DECISÃO: lidos, tratados e
   * resolvidos no período. Os abertos são o agora — não têm data de decisão.
   */
  periodo?: Periodo | null;
}) {
  const veCusto = useVeCusto();
  const [aba, setAba] = useState<Aba>('aberto');
  const [dados, setDados] = useState<DadosDosAlertas | null>(null);
  const [falhou, setFalhou] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    const r = await lerAlertas();
    if (!r.ok) return setFalhou(true);
    setFalhou(false);
    setDados(r.dados);
  }, []);
  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function decidir(a: Alerta, estado: 'lido' | 'tratado') {
    setEnviando(a.chave);
    setErro(null);
    const r = await decidirAlerta(sessao, a.chave, estado);
    setEnviando(null);
    if (!r.ok) setErro(mensagemDeFalha(comoFalha(r.falha)));
    await carregar();
    aoMudar();
  }

  const noPeriodo = (quando: string | null) => {
    if (!periodo) return true;
    if (!quando) return false;
    const d = diaDaDecisao(quando);
    return d >= periodo.de && d <= periodo.ate;
  };
  const ativos = (dados?.ativos ?? []).filter(
    (a) => a.estado === 'aberto' || noPeriodo(a.decididoEm),
  );
  const resolvidos = (dados?.resolvidos ?? []).filter((r) => noPeriodo(r.decididoEm));
  const contar = (e: Alerta['estado']) => ativos.filter((a) => a.estado === e).length;
  const lista = aba === 'resolvidos' ? [] : ativos.filter((a) => a.estado === aba);

  return (
    <div className="ac pv-alertas">
      <div className="ac-cabeca">
        <div>
          <h1 className="ac-titulo">Alertas de validade</h1>
          <div className="ac-subtitulo">
            {dados
              ? `${String(contar('aberto'))} abertos · agrupados por evento, nunca um pop-up por produto`
              : 'Carregando…'}
          </div>
        </div>
      </div>
      <div className="ac-filtros">
        {(
          [
            ['aberto', 'Abertos', contar('aberto')],
            ['lido', 'Lidos', contar('lido')],
            ['tratado', 'Tratados', contar('tratado')],
            ['resolvidos', 'Resolvidos', resolvidos.length],
          ] as const
        ).map(([id, rotulo, n]) => (
          <button
            key={id}
            type="button"
            aria-pressed={aba === id}
            className={`ac-chip${aba === id ? ' ac-chip-ativo' : ''}`}
            onClick={() => setAba(id)}
          >
            {rotulo} {n}
          </button>
        ))}
      </div>
      {falhou && (
        <ErroDaLista
          titulo="Não foi possível carregar os alertas"
          aoTentar={() => void carregar()}
        />
      )}
      {erro && (
        <div className="mc-erro" role="alert">
          {erro}
        </div>
      )}
      {!dados && !falhou && (
        <div className="ac-cartao">
          <Esqueleto linhas={4} />
        </div>
      )}

      <div className="pv-alertas-lista">
        {dados && aba !== 'resolvidos' && lista.length === 0 && (
          <div className="ap-tudo-em-dia" style={{ margin: 0 }}>
            <div className="ap-tudo-em-dia-titulo">
              {aba === 'aberto'
                ? 'Nenhum alerta aberto'
                : aba === 'lido'
                  ? 'Nenhum alerta só lido'
                  : 'Nenhum alerta tratado'}
            </div>
            <div className="ap-tudo-em-dia-texto">
              Os alertas são recalculados a cada consulta, a partir dos lotes.
            </div>
          </div>
        )}
        {lista.map((a) => (
          <div key={a.chave} className={`pv-alerta pv-alerta-${a.criticidade}`}>
            <div className="pv-alerta-topo">
              <div className="pv-alerta-titulo">{a.titulo}</div>
              <span className={`pv-selo pv-crit-${a.criticidade}`}>
                {CRITICIDADE[a.criticidade]}
              </span>
            </div>
            <div className="pv-alerta-texto">
              {a.detalhe}
              {veCusto && a.valorACusto ? ` · valor a custo ${reais(a.valorACusto)}` : ''}
            </div>
            {a.estado !== 'aberto' && a.decididoPor && a.decididoEm && (
              <div className="pv-alerta-decisao">
                {a.estado === 'lido' ? 'Lido' : 'Tratado'} por {a.decididoPor} em{' '}
                {dataHoraCurta(a.decididoEm)}
                {a.estado === 'tratado'
                  ? ' · tratado é sobre o alerta; o estado dos lotes continua o deles'
                  : ''}
              </div>
            )}
            {configura && a.estado !== 'tratado' && (
              <div className="pv-alerta-acoes">
                {a.estado === 'aberto' && (
                  <button
                    type="button"
                    className="ac-botao"
                    disabled={enviando === a.chave}
                    onClick={() => void decidir(a, 'lido')}
                  >
                    Marcar como lido
                  </button>
                )}
                <button
                  type="button"
                  className="ac-botao ac-botao-primario"
                  disabled={enviando === a.chave}
                  onClick={() => void decidir(a, 'tratado')}
                >
                  Marcar tratado
                </button>
                <span className="ac-nota">
                  tratar ≠ resolver: o alerta some sozinho quando a situação dos lotes muda
                </span>
              </div>
            )}
          </div>
        ))}
        {dados && aba === 'resolvidos' && (
          <div className="ac-cartao">
            {resolvidos.length === 0 ? (
              <div className="ac-vazio">
                <div className="ac-vazio-titulo">Nenhum alerta resolvido ainda</div>
                <div className="ac-vazio-texto">
                  Resolvido = teve decisão e a situação dos lotes mudou.
                </div>
              </div>
            ) : (
              resolvidos.map((r) => (
                <div key={r.chave} className="ac-historico pv-resolvido">
                  <span className="ac-historico-hora">{dataHoraCurta(r.decididoEm)}</span>
                  <div style={{ flex: 1 }}>
                    <b>{ROTULO_DO_TIPO[r.tipo]}</b> ·{' '}
                    {r.ultimoEstado === 'tratado' ? 'tratado' : 'lido'} por {r.decididoPor ?? '—'}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
      <div className="ac-pe-modal">
        Janela, criticidade, destinatários e silenciamento ficam em Configurações do sistema — esta
        tela só consome a regra vigente.
      </div>
    </div>
  );
}
